import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../_shared/services/http.service';
import { environment } from '../../environments/environment';
import { HttpParams } from '@angular/common/http';
import { AppService } from '../_shared/services/app.service';
import { QuoteStatus } from '../_shared/models/quote.model';

export interface SearchParams {
    salesArea: string;
    partnerId?: string;
    page?: string;
    pageSize: string;
    sortBy: string;
    sortOrder: string;
    search?: string;
    fromDate?: Date;
    toDate?: Date;
}

interface QuoteIdentifier {
    id: string;
}

interface BaseQuoteParams {
    customerPurchaseNumber: string;
}

export interface CopyQuoteParams extends BaseQuoteParams, QuoteIdentifier {}

export interface DeleteQuoteParams extends QuoteIdentifier {
    status?: QuoteStatus;
    reason: string;
}

export interface UpdateQuoteParams extends QuoteIdentifier {
    type?: 'quote' | 'template';
    templateDescription?: string;
    primarySchemes?: string;
    customerPurchaseNumber?: string;
}

export interface Pricing {
    totalGross: number;
    totalAdvantage: number;
    totalPrice: number;
    netPrice: number;
    voucherValue: number;
    extraBonus: number;
    voucherCode: string;
}

export interface QuoteItem extends Pricing {
    id: string;
    quickRef: string;
    quoteId: string;
    status: QuoteStatus;
    customerPurchaseNumber: string;
    customerNumber: string;
    customerName: string;
    submissionNumber: string;
    bindingFrom: string;
    bindingTo: string;
    createdDate: string;
    authorName: string;
    externalApp: string;

    currency: string;
    scheme?: {
        id: string;
        url: string;
        preview: string;
        dwt: string;
    };
}

export interface TemplateItem extends QuoteItem {
    description: string;
}

interface SearchResult<T> {
    totalCount: number;
    items: T[];
}

@Injectable({
    providedIn: 'root',
})
export class OverviewDataService {
    private baseUrl = `${environment.http.quotation}quotations/quote-assist`;
    constructor(private httpService: HttpService, private appService: AppService) {}

    public copy({ id, customerPurchaseNumber }: CopyQuoteParams): Observable<QuoteIdentifier> {
        const url = `${this.baseUrl}/quotes/${id}/copy`;
        return this.httpService.post<QuoteIdentifier>(url, { customerPurchaseNumber });
    }

    public getTemplate(id: string): Observable<{ customerPurchaseNumber: string; templateDescription: string }> {
        const url = `${this.baseUrl}/templates/${id}`;
        return this.httpService.get(url);
    }

    public createNew(): Observable<QuoteIdentifier> {
        const language = this.appService.getLanguage();
        const salesOrg = this.appService.getSalesOrg();
        const model = environment.quest.project;
        return this.httpService.post<QuoteIdentifier>(`${this.baseUrl}/quotes`, { model, language, salesOrg });
    }

    public delete(params: DeleteQuoteParams): Observable<QuoteIdentifier> {
        const shouldCancel = params.status !== QuoteStatus.new;
        const queryParams = shouldCancel ? { reason: params.reason } : undefined;
        return this.httpService.delete(`${this.baseUrl}/quotes/${params.id}`, queryParams);
    }

    public getAvailableCancellationReasons() {
        const url = `${environment.http.baseUrl}quote/cancel-keys?language=${this.appService.getLanguageOnly()}`;
        return this.httpService.get(url);
    }

    public useTemplate({ id, customerPurchaseNumber }: CopyQuoteParams): Observable<QuoteIdentifier> {
        const url = `${this.baseUrl}/templates/${id}/quotes`;
        return this.httpService.post(url, { customerPurchaseNumber });
    }

    public searchQuotes(parameters: SearchParams): Observable<SearchResult<QuoteItem>> {
        const url = `${this.baseUrl}/quotes`;
        const params: HttpParams = new HttpParams({
            fromObject: this.removeUndefined({
                page: parameters.page?.toString(),
                perPage: parameters.pageSize,
                sortBy: parameters.sortBy,
                sortOrder: parameters.sortOrder,
                salesOrg: this.appService.getSalesOrg(),
                partnerId: parameters.partnerId?.trim(),
                createdAfter: parameters.fromDate?.toISOString(),
                createdBefore: parameters.toDate?.toISOString(),
            }),
        });
        return this.httpService.get<SearchResult<QuoteItem>>(url, params);
    }

    public searchTemplates(parameters: SearchParams): Observable<SearchResult<QuoteItem>> {
        const url = `${this.baseUrl}/templates`;
        const params: HttpParams = new HttpParams({
            fromObject: this.removeUndefined({
                page: parameters.page?.toString(),
                perPage: parameters.pageSize,
                sortBy: parameters.sortBy,
                sortOrder: parameters.sortOrder,
                salesOrg: this.appService.getSalesOrg(),
                createdAfter: parameters.fromDate?.toISOString(),
                createdBefore: parameters.toDate?.toISOString(),
                keyword: parameters.search?.trim(),
            }),
        });
        return this.httpService.get<SearchResult<QuoteItem>>(url, params);
    }

    public updateQuote(parameters: UpdateQuoteParams) {
        const { id, ...payload } = parameters;
        const url = `${this.baseUrl}/quotes/${id}`;
        return this.httpService.patch(url, payload);
    }

    private removeUndefined(obj: { [key: string]: string }) {
        Object.keys(obj).forEach((key) => (obj[key] ? {} : delete obj[key]));
        return obj;
    }
}
